import React, { useEffect, useRef, useState } from 'react';
import { getUnreadNotificationsCount, subscribeToUserNotifications } from '../services/notificationService';
import NotificationCenter from './NotificationCenter';
import { pushToast } from '../providers/NotificationToastProvider';

const NotificationBell = ({ user }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      fetchUnreadCount();
      
      // Set up real-time listener for unread count
      let initialBatchProcessed = false;
      const seenIdsRef = seenNotificationIdsRef.current;

      const unsubscribe = subscribeToUserNotifications(user.uid, (notifications) => {
        const unread = notifications.filter(n => n.status === 'unread').length;
        setUnreadCount(unread);

        if (!initialBatchProcessed) {
          notifications.forEach((n) => seenIdsRef.add(n.id));
          initialBatchProcessed = true;
          return;
        }

        notifications.forEach((notification) => {
          if (!seenIdsRef.has(notification.id) && notification.status === 'unread') {
            seenIdsRef.add(notification.id);
            pushToast({
              title: notification.title,
              message: notification.message,
              type: mapNotificationTypeToToast(notification.type)
            });
          }
        });
      });
      
      return () => {
        if (unsubscribe) unsubscribe();
        seenIdsRef.clear();
      };
    }
  }, [user]);

  const seenNotificationIdsRef = useRef(new Set());

  const mapNotificationTypeToToast = (type) => {
    switch (type) {
      case 'application_status_update':
        return 'success';
      case 'application_submitted':
        return 'success';
      case 'new_application':
        return 'info';
      case 'new_job':
        return 'info';
      default:
        return 'info';
    }
  };

  const fetchUnreadCount = async () => {
    if (!user?.uid) return;
    
    try {
      const result = await getUnreadNotificationsCount(user.uid);
      if (result.success) {
        setUnreadCount(result.count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const handleBellClick = () => {
    setShowNotifications(true);
  };

  const handleCloseNotifications = () => {
    setShowNotifications(false);
    // Refresh unread count when closing
    fetchUnreadCount();
  };

  if (!user) return null;

  return (
    <>
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
        title="Notifications"
      >
        <svg 
          className="w-6 h-6" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
          />
        </svg>
        
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <NotificationCenter
        user={user}
        isOpen={showNotifications}
        onClose={handleCloseNotifications}
      />
    </>
  );
};

export default NotificationBell;
